import { Container, Flex, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useLotNames } from "~/hooks/Gmp/useLotNames";
import { FormInputCategory } from "~/utils/Types";

import SmallButton from "./Button/SmallButton";
import Dialog from "./Dialog";
import DialogWrapper from "./DialogWrapper";
import Form from "./Form";

type LotNameEditDialogProps = {
  open: boolean;
  onClose: () => void;
  lotReference: string;
  killDate: string;
  currentName?: string;
  headCount?: number;
};

const LotNameEditDialog = ({
  open,
  onClose,
  lotReference,
  killDate,
  currentName,
  headCount,
}: LotNameEditDialogProps) => {
  const { t } = useTranslation("management");
  const {
    getNameRecord,
    addNameMutation,
    updateNameMutation,
    deleteNameMutation,
    mutationLoading,
  } = useLotNames();

  const existingRecord = getNameRecord(lotReference, killDate);

  const handleReset = () => {
    if (existingRecord) {
      deleteNameMutation.mutate(
        { data: { id: existingRecord._id } },
        { onSuccess: onClose },
      );
    }
  };

  const handleSubmit = (data: { name: string }) => {
    const trimmedName = data.name.trim();

    // If name is empty and record exists, delete it
    if (!trimmedName && existingRecord) {
      deleteNameMutation.mutate(
        { data: { id: existingRecord._id } },
        { onSuccess: onClose },
      );
      return;
    }

    // If name is empty and no record exists, just close
    if (!trimmedName) {
      onClose();
      return;
    }

    if (existingRecord) {
      updateNameMutation.mutate(
        { data: { id: existingRecord._id, name: trimmedName } },
        { onSuccess: onClose },
      );
    } else {
      addNameMutation.mutate(
        { data: { lotReference, killDate, name: trimmedName } },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} isTransparent>
      <DialogWrapper onCancel={onClose}>
        <Container size={600} w="100%">
          <Flex direction="column" flex={1} py={24} gap={16}>
            <Text size="lg" fw={600}>
              {t("lotNames.editName")}
            </Text>
            <Text size="sm" c="var(--mantine-color-text-secondary)">
              {t("lotNames.lotReference")}: {lotReference}
              {headCount !== undefined && ` | Head: ${headCount}`}
            </Text>
            <Form
              loading={mutationLoading}
              onSubmit={handleSubmit}
              buttonText={t("lotNames.update")}
              buttonSize="small"
              buttonFullWidth={false}
              extraButton={
                existingRecord ? (
                  <SmallButton
                    title={t("lotNames.reset")}
                    variant="outline"
                    onClick={handleReset}
                    loading={mutationLoading}
                  />
                ) : undefined
              }
              inputs={[
                {
                  name: "name",
                  label: t("lotNames.name"),
                  category: FormInputCategory.TEXT_FIELD,
                  defaultValue: currentName || existingRecord?.name || "",
                  required: false,
                  avoidValidation: true,
                  inputProps: { maxLength: 50, autoComplete: "off" },
                },
              ]}
            />
          </Flex>
        </Container>
      </DialogWrapper>
    </Dialog>
  );
};

export default LotNameEditDialog;
